<template>
    <div class="relative min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filterByEntity" @change="onFilterChange"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                            <option value="">All Entities</option>
                            <option v-for="entity in entities" :key="entity.value" :value="entity.value">
                                {{ entity.label }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                        <select v-model="filterByRole" @change="onFilterChange"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                            <option value="">All Roles</option>
                            <option v-for="role in roles" :key="role.name" :value="role.name">
                                {{ role.display_name || role.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">User Management</span>
                </nav>

                <!-- Page Header -->
                <div class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 mb-4">
                    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div class="flex-1">
                            <h1 class="text-3xl font-bold text-blue-900 flex items-center gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                    class="h-8 w-8">
                                    <path
                                        d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" />
                                </svg>
                                User Management
                                <div v-if="isLoading" class="ml-2">
                                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                </div>
                            </h1>
                            <p class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
                                Comprehensive user administration portal to view, manage, and organize all system users
                                across
                                different roles, entities, and departments.
                            </p>
                        </div>
                        <button v-if="canCreateUser" type="button" @click="openAddUserModal"
                            class="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm shadow-sm transition-colors">
                            <font-awesome-icon :icon="['fas', 'user-plus']" class="text-base" />
                            Add User
                        </button>
                    </div>
                </div>

                <!-- Engagement Insights -->
                <div class="mb-6">
                    <div v-if="isHighlightsLoading"
                        class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 animate-pulse">
                        <div class="h-6 w-1/3 bg-gray-200 rounded mb-4"></div>
                        <div class="h-4 w-2/3 bg-gray-200 rounded"></div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                            <div v-for="i in 4" :key="'summary-skeleton-' + i" class="h-24 bg-gray-100 rounded-2xl">
                            </div>
                        </div>
                    </div>
                    <div v-else-if="hasHighlights"
                        class="relative overflow-hidden rounded-3xl engagement-gradient text-white p-8 shadow-xl">
                        <div class="absolute inset-0 opacity-25"
                            style="background: radial-gradient(circle at top, rgba(255, 255, 255, 0.25), transparent 60%);">
                        </div>
                        <div class="relative flex flex-col lg:flex-row gap-10">
                            <div class="lg:w-7/12">
                                <p class="text-sm uppercase tracking-widest text-blue-100 font-semibold">Top performer
                                </p>
                                <h2 class="text-3xl font-bold mt-3">
                                    <span v-if="primaryHighlight">{{ primaryHighlight.user_name }}</span>
                                    <span v-else>User Engagement Insights</span>
                                </h2>
                                <p class="text-blue-100 mt-2 text-sm" v-if="primaryHighlight">
                                    {{ primaryHighlight.metric }} • {{ formatNumber(primaryHighlight.count) }}
                                    contributions
                                </p>
                                <div class="mt-6 flex items-center gap-4" v-if="primaryHighlight">
                                    <div
                                        class="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-semibold">
                                        {{ getInitials(primaryHighlight.user_name) }}
                                    </div>
                                    <div>
                                        <p class="text-lg font-semibold text-white">{{ primaryHighlight.role ||
                                            'Contributor' }}</p>
                                        <p class="text-sm text-blue-100">{{ formatEntity(primaryHighlight.entity) }}</p>
                                    </div>
                                </div>
                                <p class="mt-6 text-sm text-blue-50 leading-relaxed">
                                    Spotlighting the most engaged contributors across Think Tanks, Engagement Hubs,
                                    Innovations, and Student Life for the last {{ engagementHighlights.windowLabel }}.
                                </p>
                            </div>
                            <div class="lg:w-5/12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div v-for="card in summaryCards" :key="card.title"
                                    class="bg-white/15 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                                    <p class="text-sm text-blue-100 font-medium">{{ card.title }}</p>
                                    <p class="text-3xl font-semibold mt-2">{{ formatNumber(card.value) }}</p>
                                    <div class="mt-3 flex items-center gap-2 text-sm"
                                        :class="deltaTrendClass(card.delta)"
                                        v-if="card.delta !== null && card.delta !== undefined">
                                        <svg v-if="card.delta >= 0" class="h-4 w-4" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M5 10l7-7 7 7M12 3v18" />
                                        </svg>
                                        <svg v-else class="h-4 w-4" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M19 14l-7 7-7-7M12 21V3" />
                                        </svg>
                                        <span>{{ card.delta >= 0 ? '+' : '' }}{{ formatNumber(card.delta) }}</span>
                                        <span v-if="card.delta_percent !== null && card.delta_percent !== undefined">
                                            ({{ card.delta_percent }}%)
                                        </span>
                                    </div>
                                    <p class="text-xs text-blue-100 mt-1" v-if="card.description">{{ card.description }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="bg-white border border-gray-200 rounded-3xl shadow-md p-8">
                        <p class="text-gray-600 text-sm">Engagement insights will appear here once data is available.
                        </p>
                    </div>
                </div>

                <div v-if="hasHighlights" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
                    <div v-for="card in highlightCards" :key="card.key"
                        class="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 flex flex-col gap-4 hover:shadow-lg transition-shadow duration-200">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ card.title }}
                                </p>
                                <p class="text-lg font-semibold text-gray-900 mt-1" v-if="card.data">{{
                                    card.data.user_name }}</p>
                                <p class="text-sm text-gray-400 mt-1" v-else>Data not available</p>
                            </div>
                            <div :class="['h-11 w-11 rounded-xl flex items-center justify-center', card.iconBg]">
                                <font-awesome-icon :icon="card.icon" class="text-lg" />
                            </div>
                        </div>
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-3xl font-bold text-gray-900">
                                    {{ card.data ? formatNumber(card.data.count) : '--' }}
                                </p>
                                <p class="text-xs text-gray-500" v-if="card.data && card.data.metric">{{
                                    card.data.metric }}</p>
                            </div>
                            <div class="text-right" v-if="card.data">
                                <p class="text-sm font-semibold text-blue-700">{{ formatEntity(card.data.entity) }}</p>
                                <p class="text-xs text-gray-500">{{ card.data.role || 'Contributor' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="hasHighlights" class="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
                    <div class="xl:col-span-2 bg-white border border-gray-200 rounded-3xl shadow-sm p-6">
                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">Activity Leaderboard</h3>
                                <p class="text-sm text-gray-500">Top 5 contributors over the last {{
                                    engagementHighlights.windowLabel }}</p>
                            </div>
                            <div class="flex items-center gap-2">
                                <label
                                    class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Window</label>
                                <select v-model="highlightWindow" @change="onHighlightWindowChange"
                                    class="border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                    <option value="7d">Last 7 days</option>
                                    <option value="30d">Last 30 days</option>
                                    <option value="90d">Last 90 days</option>
                                    <option value="365d">Last 12 months</option>
                                </select>
                            </div>
                        </div>
                        <div v-if="leaderboardChartSeries.length && leaderboardChartSeries[0].data.length">
                            <apexchart type="bar" height="320" :options="leaderboardChartOptions"
                                :series="leaderboardChartSeries" />
                        </div>
                        <div v-else class="py-12 text-center text-gray-500">
                            No activity data found for the selected window.
                        </div>
                        <ul class="mt-6 divide-y divide-gray-100" v-if="activityLeaderboardList.length">
                            <li v-for="(item, index) in activityLeaderboardList" :key="'leaderboard-' + index"
                                class="py-3 flex items-center justify-between">
                                <div class="flex items-center gap-3">
                                    <span
                                        class="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold">
                                        {{ index + 1 }}
                                    </span>
                                    <div>
                                        <p class="text-sm font-semibold text-gray-900">{{ item.user_name }}</p>
                                        <p class="text-xs text-gray-500">{{ formatEntity(item.entity) }} • {{ item.role
                                            || 'Contributor' }}</p>
                                    </div>
                                </div>
                                <p class="text-base font-semibold text-gray-900">{{ formatNumber(item.value) }}</p>
                            </li>
                        </ul>
                    </div>
                    <div class="bg-white border border-gray-200 rounded-3xl shadow-sm p-6">
                        <h3 class="text-lg font-semibold text-gray-900">Contribution Mix</h3>
                        <p class="text-sm text-gray-500 mb-6">Where users are most active</p>
                        <div v-if="distributionChartSeries.length">
                            <apexchart type="donut" height="320" :options="distributionChartOptions"
                                :series="distributionChartSeries" />
                        </div>
                        <div v-else class="py-12 text-center text-gray-500">
                            No contribution data available.
                        </div>
                        <ul class="mt-6 space-y-3"
                            v-if="engagementHighlights && engagementHighlights.contributionDistribution">
                            <li v-for="slice in engagementHighlights.contributionDistribution" :key="slice.label"
                                class="flex items-center justify-between text-sm">
                                <span class="text-gray-600">{{ slice.label }}</span>
                                <span class="font-semibold text-gray-900">{{ formatNumber(slice.value) }}</span>
                            </li>
                        </ul>
                        <div class="mt-8 pt-6 border-t border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900">Top Departments</h3>
                            <p class="text-sm text-gray-500 mb-4">Departments with most active users (last {{
                                engagementHighlights && engagementHighlights.windowLabel ?
                                    engagementHighlights.windowLabel : '30 days' }})</p>
                            <ul class="space-y-3" v-if="topDepartmentsList.length">
                                <li v-for="(dept, idx) in topDepartmentsList" :key="dept.label"
                                    class="flex items-center justify-between text-sm">
                                    <div class="flex items-center gap-2">
                                        <span
                                            class="h-6 w-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-semibold">
                                            {{ idx + 1 }}
                                        </span>
                                        <span class="text-gray-700">{{ dept.label }}</span>
                                    </div>
                                    <span @click="openDepartmentUsers(dept)"
                                        class="font-semibold text-gray-900 text-blue-600 hover:text-blue-700 cursor-pointer hover:underline">
                                        {{ formatNumber(dept.value) }} users
                                    </span>
                                </li>
                            </ul>
                            <p v-else class="py-6 text-center text-gray-500 text-sm">
                                No department data available.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Top Users in Each Department (by Entity) -->
                <div v-if="appliedEntityForDepartments" class="mb-6">
                    <div class="bg-white border border-gray-200 rounded-3xl shadow-sm p-6">
                        <h3 class="text-lg font-semibold text-gray-900 mb-1">Top Users in Each Department</h3>
                        <p class="text-sm text-gray-500 mb-4">{{ textForEntityDepartments }}</p>
                        <div v-if="isEntityDepartmentsLoading" class="py-12 text-center">
                            <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                            <p class="text-gray-500 mt-2">Loading departments...</p>
                        </div>
                        <div
                            v-else-if="entityDepartmentsChartSeries.length && entityDepartmentsChartSeries[0].data.length">
                            <apexchart type="bar" :height="entityDepartmentsChartHeight"
                                :options="entityDepartmentsChartOptions" :series="entityDepartmentsChartSeries"
                                @dataPointSelection="onDepartmentChartClick" />
                        </div>
                        <div v-else-if="appliedEntityForDepartments && !isEntityDepartmentsLoading"
                            class="py-12 text-center text-gray-500">
                            No departments found for this entity.
                        </div>
                    </div>
                </div>

                <!-- Advanced Search -->
                <div class="mb-4">
                    <UserManagementAdvancedSearch :initial-basic-search="searchText"
                        :initial-advanced-search="advancedSearch" :roles="roles" :entities="entities"
                        @on-basic-search="onBasicSearch" @on-advanced-search="onAdvancedSearch"
                        @on-clear-search="clearSearch" />
                </div>

                <!-- Users Grid -->
                <UserGrid :users="filteredUsers" :isLoading="isLoading" @view-user="viewUser" @edit-user="editUser"
                    @impersonate-user="impersonateUser" />

                <!-- Pagination -->
                <div v-if="!isLoading && paginatorInfo.total > 0" class="bg-white rounded-lg shadow-sm p-6">
                    <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                </div>
            </div>
        </div>

        <!-- Modal for View User -->
        <transition name="fade">
            <base-full-modal v-if="isViewUser" title="User Details" @close="closeModal">
                <UserView :user="selectedUser" @close="closeModal" @impersonate-user="impersonateUser" />
            </base-full-modal>
        </transition>

        <!-- Add User Modal -->
        <transition name="fade">
            <base-modal v-if="showAddUserModal" title="Add User" @close="closeAddUserModal">
                <div class="px-6 pb-6 pt-2">
                    <div v-if="addUserOptionsLoading" class="py-16 text-center text-gray-500">
                        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                        <p class="mt-3">Loading form options…</p>
                    </div>
                    <form v-else @submit.prevent="submitAddUser" class="space-y-5">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Name <span
                                        class="text-red-500">*</span></label>
                                <input v-model="addUserForm.name" type="text" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Email <span
                                        class="text-red-500">*</span></label>
                                <input v-model="addUserForm.email" type="email" required autocomplete="off"
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div class="md:col-span-2">
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Phone number</label>
                                <input v-model="addUserForm.phone_number" type="tel" autocomplete="tel"
                                    placeholder="Optional"
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent md:max-w-md" />
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Password <span
                                        class="text-red-500">*</span></label>
                                <input v-model="addUserForm.password" type="password" required minlength="6"
                                    autocomplete="new-password"
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                <p class="text-xs text-gray-500 mt-1">Minimum 6 characters.</p>
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Role <span
                                        class="text-red-500">*</span></label>
                                <select v-model="addUserForm.role" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                    <option disabled value="">Select role</option>
                                    <option v-for="r in addUserRolesOptions" :key="'add-role-' + r.name"
                                        :value="r.name">
                                        {{ r.display_name || r.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div class="min-w-0">
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                                <multiselect v-model="addUserForm.entitySelections" :options="addUserEntityOptions"
                                    :multiple="true" :close-on-select="false" label="label" track-by="value"
                                    placeholder="Select one or more entities" />
                            </div>
                            <div class="min-w-0">
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
                                <multiselect v-model="addUserForm.campusSelections" :options="addUserCampusOptions"
                                    :disabled="!addUserForm.entitySelections.length" :loading="addUserCampusesLoading"
                                    :multiple="true" :close-on-select="false" label="label" track-by="value"
                                    :placeholder="addUserForm.entitySelections.length ? 'Select one or more campuses' : 'Select entity first'" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Position</label>
                                <input v-model="addUserForm.position" type="text"
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div class="flex items-center gap-3 pt-6 md:pt-8">
                                <input id="add-user-receive-email" v-model="addUserForm.is_receive_email"
                                    type="checkbox"
                                    class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <label for="add-user-receive-email" class="text-sm font-medium text-gray-700">Receive
                                    email notifications</label>
                            </div>
                        </div>

                        <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                            <p class="text-sm font-semibold text-gray-800">Are you a faculty?</p>
                            <div class="flex flex-wrap gap-4">
                                <label class="inline-flex items-center gap-2 cursor-pointer">
                                    <input v-model="addUserForm.is_faculty" type="radio" :value="false"
                                        class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                    <span class="text-sm text-gray-700">No</span>
                                </label>
                                <label class="inline-flex items-center gap-2 cursor-pointer">
                                    <input v-model="addUserForm.is_faculty" type="radio" :value="true"
                                        class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                    <span class="text-sm text-gray-700">Yes</span>
                                </label>
                            </div>
                            <div v-if="addUserForm.is_faculty" class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Staff ID</label>
                                    <input v-model="addUserForm.staff_id" type="text"
                                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Faculty type <span
                                            class="text-red-500">*</span></label>
                                    <select v-model="addUserForm.faculty_type"
                                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                        <option value="">Select type</option>
                                        <option value="academic">Academic</option>
                                        <option value="admin">Admin</option>
                                        <option value="academic_support">Academic Support</option>
                                    </select>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Department</label>
                                    <p class="text-xs text-gray-500 mb-1">Stored as program on the faculty record.</p>
                                    <input v-model="addUserForm.department" type="text"
                                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                            </div>
                        </div>

                        <div class="flex justify-end gap-3 pt-2">
                            <button type="button" @click="closeAddUserModal"
                                class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50">
                                Cancel
                            </button>
                            <button type="submit" :disabled="addUserSubmitLoading"
                                class="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed">
                                <span v-if="addUserSubmitLoading">Saving…</span>
                                <span v-else>Create user</span>
                            </button>
                        </div>
                    </form>
                </div>
            </base-modal>
        </transition>

        <!-- Modal for Department Users -->
        <transition name="fade">
            <base-modal v-if="showDepartmentUsersModal" :title="departmentUsersModalTitle"
                @close="closeDepartmentUsersModal">
                <div class="p-4">
                    <div v-if="isDepartmentUsersLoading" class="py-12 text-center">
                        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                        <p class="text-gray-500 mt-2">Loading users...</p>
                    </div>
                    <div v-else-if="departmentUsersList.length">
                        <ul class="divide-y divide-gray-200">
                            <li v-for="u in departmentUsersList" :key="u.id || u.user_id"
                                class="py-4 flex items-center gap-4 rounded-lg px-4 -mx-4">
                                <img :src="profilePicture(u.profile_picture_url)" :alt="u.name || u.user_name"
                                    class="w-12 h-12 rounded-full object-cover border-2 border-gray-200" />
                                <div class="flex-1 min-w-0">
                                    <p class="font-semibold text-gray-900">{{ u.name || u.user_name }}</p>
                                    <p class="text-sm text-gray-500">{{ u.role_display_name || u.role_name || u.role ||
                                        '—' }} • {{ formatEntity(u.entity) }}</p>
                                    <p v-if="u.activity_count !== undefined && u.activity_count !== null"
                                        class="text-xs text-blue-600 font-medium mt-1">{{ formatNumber(u.activity_count)
                                        }} actions</p>
                                </div>
                                <button v-if="u.token"
                                    @click="closeDepartmentUsersModal(); $router.push('/profile/' + u.token)"
                                    class="text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
                                    View
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div v-else class="py-12 text-center text-gray-500">
                        No users found.
                    </div>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import Pagination from '../../components/shared/pagination.vue';
import BaseModal from '../../components/ui/BaseModal.vue';
import UserManagementAdvancedSearch from './components/UserManagementAdvancedSearch.vue';
import UserGrid from './components/UserGrid.vue';
import UserView from './view.vue';

const GET_ALL_USERS_QUERY = gql`
    query GetAllUsers($first: Int!, $page: Int!, $search: String, $role: String, $entity: String, $campus: String) {
        getAllUsers(first: $first, page: $page, search: $search, role: $role, entity: $entity, campus: $campus) {
            error
            error_code
            message
            paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
            }
            data {
                id
                token
                name
                email
                phone_number
                position
                entity
                campuses
                role_name
                role_display_name
                profile_picture_url
                login_status
                userDepartments {
                    department
                }
            }
        }
    }
`;

const GET_USER_ROLES_QUERY = gql`
    query GetUserRoles {
        getUserRoles {
            error
            error_code
            message
            data {
                id
                name
                display_name
            }
        }
    }
`;

const GET_USER_ENGAGEMENT_HIGHLIGHTS = gql`
    query GetUserEngagementHighlights($window: String, $entity: String, $role: String) {
        getUserEngagementHighlights(window: $window, entity: $entity, role: $role) {
            error
            error_code
            message
            data {
                windowKey
                windowLabel
                summaryCards {
                    title
                    value
                    delta
                    delta_percent
                    description
                }
                highlights {
                    mostActiveUser {
                        user_id
                        user_name
                        role
                        entity
                        count
                        metric
                    }
                    mostThinkTanksUser {
                        user_id
                        user_name
                        role
                        entity
                        count
                        metric
                    }
                    mostAcademicEngagementUser {
                        user_id
                        user_name
                        role
                        entity
                        count
                        metric
                    }
                    mostInnovationUser {
                        user_id
                        user_name
                        role
                        entity
                        count
                        metric
                    }
                    mostStudentLifeUser {
                        user_id
                        user_name
                        role
                        entity
                        count
                        metric
                    }
                }
                activityLeaderboard {
                    user_id
                    user_name
                    role
                    entity
                    value
                }
                contributionDistribution {
                    label
                    value
                }
                topDepartments {
                    label
                    value
                }
            }
        }
    }
`;

const GET_DEPARTMENT_ACTIVE_USERS = gql`
    query GetDepartmentActiveUsers($department: String!, $window: String, $entity: String, $role: String) {
        getDepartmentActiveUsers(department: $department, window: $window, entity: $entity, role: $role) {
            error
            error_code
            message
            data {
                id
                token
                name
                profile_picture_url
                role
                role_name
                role_display_name
                entity
            }
        }
    }
`;

const GET_ENTITY_DEPARTMENTS_WITH_TOP_USERS = gql`
    query GetEntityDepartmentsWithTopUsers($entity: String!, $window: String, $role: String, $limitPerDepartment: Int) {
        getEntityDepartmentsWithTopUsers(entity: $entity, window: $window, role: $role, limitPerDepartment: $limitPerDepartment) {
            error
            error_code
            message
            data {
                entity
                departments {
                    department
                    topUsers {
                        user_id
                        user_name
                        token
                        profile_picture_url
                        role
                        entity
                        activity_count
                    }
                }
            }
        }
    }
`;

const GET_USER_MANAGEMENT_ADD_USER_OPTIONS = gql`
    query GetUserManagementAddUserOptions {
        getUserManagementAddUserOptions {
            error
            error_code
            message
            data {
                entities {
                    value
                    label
                }
                roles {
                    id
                    name
                    display_name
                    guard_name
                }
            }
        }
    }
`;

const GET_USER_MANAGEMENT_CAMPUSES_FOR_ENTITIES = gql`
    query GetUserManagementCampusesForEntities($entities: [String!]!) {
        getUserManagementCampusesForEntities(entities: $entities) {
            error
            error_code
            message
            data {
                value
                label
            }
        }
    }
`;

const CREATE_USER_MANAGEMENT_USER = gql`
    mutation CreateUserManagementUser($input: CreateUserManagementUserInput!) {
        createUserManagementUser(input: $input) {
            error
            error_code
            message
            data {
                id
            }
        }
    }
`;

export default {
    name: "user-management",
    components: {
        Pagination,
        BaseModal,
        Multiselect,
        UserManagementAdvancedSearch,
        UserGrid,
        UserView,
    },
    data() {
        return {
            showFilter: false,
            users: [],
            first: 12,
            page: 1,
            searchText: '',
            advancedSearch: {
                role: "",
                entity: "",
                campus: "",
            },
            filterByRole: "",
            filterByEntity: "",
            entities: [
                { value: 'IAT', label: 'IAT' },
                { value: 'FCHS', label: 'FCHS' },
                { value: 'ADPOLY', label: 'ADPOLY' },
                { value: 'ATHS', label: 'ATS' },
                { value: 'ADVETI', label: 'ADVETI' },
            ],
            roles: [],
            isLoading: false,
            isViewUser: false,
            selectedUser: null,
            highlightWindow: '30d',
            showDepartmentUsersModal: false,
            selectedDepartment: null,
            departmentUsersList: [],
            isDepartmentUsersLoading: false,
            entityDepartmentsWithTopUsers: [],
            isEntityDepartmentsLoading: false,
            appliedEntityForDepartments: '',
            engagementHighlights: null,
            isHighlightsLoading: true,
            leaderboardChartOptions: {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                    fontFamily: 'Inter, sans-serif'
                },
                colors: ['#3B82F6'],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '45%',
                    }
                },
                dataLabels: {
                    enabled: true,
                    offsetX: 10,
                    style: {
                        fontSize: '12px',
                        fontWeight: '600'
                    }
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '500'
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '600'
                        }
                    }
                },
                grid: {
                    borderColor: '#E5E7EB',
                    strokeDashArray: 4
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " actions";
                        }
                    }
                }
            },
            leaderboardChartSeries: [{
                name: 'Activity Logs',
                data: []
            }],
            distributionChartOptions: {
                chart: {
                    type: 'donut'
                },
                labels: [],
                legend: {
                    position: 'bottom',
                    fontSize: '13px',
                    fontWeight: 600
                },
                colors: ['#2563EB', '#7C3AED', '#F97316', '#10B981'],
                stroke: {
                    width: 0
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        if (isNaN(val)) {
                            return "0%";
                        }
                        return val.toFixed(0) + "%";
                    }
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    formatter: function (w) {
                                        if (w && w.globals && w.globals.seriesTotals) {
                                            return w.globals.seriesTotals.reduce(function (acc, val) {
                                                return acc + val;
                                            }, 0);
                                        }
                                        return 0;
                                    }
                                }
                            }
                        }
                    }
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " contributions";
                        }
                    }
                }
            },
            distributionChartSeries: [],
            showAddUserModal: false,
            addUserOptionsLoading: false,
            addUserSubmitLoading: false,
            addUserEntityOptions: [],
            addUserCampusOptions: [],
            addUserCampusesLoading: false,
            addUserRolesOptions: [],
            addUserForm: {
                name: '',
                email: '',
                phone_number: '',
                password: '',
                entitySelections: [],
                campusSelections: [],
                role: '',
                department: '',
                position: '',
                is_receive_email: false,
                is_faculty: false,
                faculty_type: '',
                staff_id: '',
            },
        };
    },
    computed: {
        textForEntityDepartments() {
            return 'Total users by department (last ' + this.highlightWindowLabel + '). Click a bar to view users.';
        },
        highlightWindowLabel() {
            return this.highlightWindow === '7d' ? '7 days' : this.highlightWindow === '90d' ? '90 days' : this.highlightWindow === '365d' ? '12 months' : '30 days';
        },
        role() {
            return this.$store.getters.role;
        },
        canCreateUser() {
            const permissions = this.$store.getters.permissions || '';
            const permissionsArray = permissions.split(',');
            var i;
            for (i = 0; i < permissionsArray.length; i++) {
                if (permissionsArray[i].trim() === 'user-management-create') {
                    return true;
                }
            }
            return false;
        },
        filteredUsers() {
            return this.users.data || [];
        },
        paginatorInfo() {
            return this.users.paginatorInfo || {
                total: 0,
                count: 0,
                perPage: 12,
                currentPage: 1,
                lastPage: 1,
                hasMorePages: false,
                firstItem: null,
                lastItem: null,
            };
        },
        summaryCards() {
            if (this.engagementHighlights && this.engagementHighlights.summaryCards) {
                return this.engagementHighlights.summaryCards;
            }
            return [];
        },
        hasHighlights() {
            return !!(this.engagementHighlights && this.engagementHighlights.highlights);
        },
        primaryHighlight() {
            if (!this.hasHighlights) {
                return null;
            }
            const highlightMap = this.engagementHighlights.highlights;
            if (highlightMap.mostActiveUser) {
                return highlightMap.mostActiveUser;
            }
            if (highlightMap.mostThinkTanksUser) {
                return highlightMap.mostThinkTanksUser;
            }
            if (highlightMap.mostAcademicEngagementUser) {
                return highlightMap.mostAcademicEngagementUser;
            }
            if (highlightMap.mostInnovationUser) {
                return highlightMap.mostInnovationUser;
            }
            if (highlightMap.mostStudentLifeUser) {
                return highlightMap.mostStudentLifeUser;
            }
            return null;
        },
        highlightCards() {
            const highlightData = this.hasHighlights ? this.engagementHighlights.highlights : {};
            const configs = [
                { key: 'mostActiveUser', title: 'Most Active User', icon: ['fas', 'chart-line'], iconBg: 'bg-blue-100 text-blue-600' },
                { key: 'mostThinkTanksUser', title: 'Think Tanks Champion', icon: ['fas', 'brain'], iconBg: 'bg-indigo-100 text-indigo-600' },
                { key: 'mostAcademicEngagementUser', title: 'Engagement Ambassador', icon: ['fas', 'handshake'], iconBg: 'bg-amber-100 text-amber-600' },
                { key: 'mostInnovationUser', title: 'Innovation Leader', icon: ['fas', 'lightbulb'], iconBg: 'bg-emerald-100 text-emerald-600' },
                { key: 'mostStudentLifeUser', title: 'Student Life Advocate', icon: ['fas', 'users'], iconBg: 'bg-pink-100 text-pink-600' },
            ];
            return configs.map(function (config) {
                return {
                    key: config.key,
                    title: config.title,
                    icon: config.icon,
                    iconBg: config.iconBg,
                    data: highlightData && highlightData[config.key] ? highlightData[config.key] : null,
                };
            });
        },
        activityLeaderboardList() {
            if (this.engagementHighlights && this.engagementHighlights.activityLeaderboard) {
                return this.engagementHighlights.activityLeaderboard;
            }
            return [];
        },
        topDepartmentsList() {
            if (this.engagementHighlights && this.engagementHighlights.topDepartments) {
                return this.engagementHighlights.topDepartments;
            }
            return [];
        },
        departmentUsersModalTitle() {
            if (this.selectedDepartment) {
                return 'Users in ' + this.selectedDepartment.label;
            }
            return 'Department Users';
        },
        entityDepartmentsChartHeight() {
            const count = (this.entityDepartmentsWithTopUsers || []).length;
            const minHeight = 200;
            const maxHeight = 1600;
            const pixelsPerBar = 42;
            return Math.min(Math.max(count * pixelsPerBar, minHeight), maxHeight);
        },
        entityDepartmentsChartSeries() {
            const depts = this.entityDepartmentsWithTopUsers || [];
            const data = depts.map(function (d) {
                return (d.topUsers || []).length;
            });
            return [{ name: 'Total Users', data: data }];
        },
        entityDepartmentsChartOptions() {
            const depts = this.entityDepartmentsWithTopUsers || [];
            const categories = depts.map(function (d) {
                const name = d.department || '';
                return name.length > 25 ? name.substring(0, 25) + '…' : name;
            });
            return {
                chart: { type: 'bar', toolbar: { show: false } },
                colors: ['#3B82F6'],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '45%',
                    }
                },
                dataLabels: {
                    enabled: true,
                    offsetX: 10,
                },
                xaxis: { categories: categories },
                grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                tooltip: {
                    y: { formatter: function (val) { return val + (val === 1 ? ' user' : ' users'); } }
                }
            };
        },
    },
    watch: {
        'addUserForm.entitySelections': {
            deep: true,
            handler: function () {
                this.onAddUserEntitySelectionsChange();
            },
        },
    },
    created() {
        this.loadUsers();
        this.loadRoles();
        this.loadUserHighlights();
    },
    methods: {
        async loadUserHighlights() {
            this.isHighlightsLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_ENGAGEMENT_HIGHLIGHTS,
                    variables: {
                        window: this.highlightWindow,
                        entity: this.filterByEntity || this.advancedSearch.entity || null,
                        role: this.filterByRole || this.advancedSearch.role || null,
                    },
                    fetchPolicy: 'network-only',
                });
                if (response.data.getUserEngagementHighlights.error) {
                    console.error("Failed to load engagement highlights:", response.data.getUserEngagementHighlights.message);
                    this.engagementHighlights = null;
                } else {
                    this.engagementHighlights = response.data.getUserEngagementHighlights.data || null;
                    this.syncHighlightVisuals();
                }
            } catch (error) {
                console.error("Failed to load engagement highlights:", error);
                this.engagementHighlights = null;
            }
            this.isHighlightsLoading = false;
        },
        syncHighlightVisuals() {
            if (!this.engagementHighlights) {
                this.leaderboardChartSeries = [{
                    name: 'Activity Logs',
                    data: [],
                }];
                this.distributionChartSeries = [];
                const resetLeaderboardOptions = Object.assign({}, this.leaderboardChartOptions);
                resetLeaderboardOptions.xaxis = Object.assign({}, resetLeaderboardOptions.xaxis || {}, { categories: [] });
                this.leaderboardChartOptions = resetLeaderboardOptions;
                const resetDistributionOptions = Object.assign({}, this.distributionChartOptions);
                resetDistributionOptions.labels = [];
                this.distributionChartOptions = resetDistributionOptions;
                return;
            }

            const leaderboard = this.engagementHighlights.activityLeaderboard || [];
            const categories = leaderboard.map(function (item) {
                const name = item.user_name || 'Unknown';
                return name.length > 18 ? name.substring(0, 18) + '…' : name;
            });
            const leaderboardOptions = Object.assign({}, this.leaderboardChartOptions);
            leaderboardOptions.xaxis = Object.assign({}, leaderboardOptions.xaxis || {}, { categories });
            this.leaderboardChartOptions = leaderboardOptions;
            this.leaderboardChartSeries = [{
                name: 'Activity Logs',
                data: leaderboard.map(function (item) {
                    return item.value || 0;
                }),
            }];

            const slices = this.engagementHighlights.contributionDistribution || [];
            const distributionOptions = Object.assign({}, this.distributionChartOptions);
            distributionOptions.labels = slices.map(function (slice) {
                return slice.label;
            });
            this.distributionChartOptions = distributionOptions;
            this.distributionChartSeries = slices.map(function (slice) {
                return slice.value || 0;
            });
        },
        onHighlightWindowChange() {
            this.loadUserHighlights();
            if (this.appliedEntityForDepartments) {
                this.loadEntityDepartmentsWithTopUsers();
            }
        },
        formatNumber(value) {
            if (value === null || value === undefined) {
                return '0';
            }
            const number = Number(value);
            if (Number.isNaN(number)) {
                return value;
            }
            return number.toLocaleString('en-US');
        },
        getInitials(name) {
            if (!name) {
                return '--';
            }
            const parts = name.trim().split(' ').filter(function (part) {
                return part;
            });
            if (!parts.length) {
                return '--';
            }
            if (parts.length === 1) {
                return parts[0].substring(0, 2).toUpperCase();
            }
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        },
        formatEntity(entity) {
            if (!entity) {
                return '—';
            }
            const parts = entity.split(',');
            if (!parts.length) {
                return entity;
            }
            return parts[0].trim();
        },
        deltaTrendClass(delta) {
            if (delta === null || delta === undefined) {
                return 'text-gray-100';
            }
            return delta >= 0 ? 'text-emerald-200' : 'text-rose-200';
        },
        async loadUsers() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_ALL_USERS_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        search: this.searchText || null,
                        role: this.advancedSearch.role || this.filterByRole || null,
                        entity: this.advancedSearch.entity || this.filterByEntity || null,
                        campus: this.advancedSearch.campus || null,
                    },
                    fetchPolicy: 'network-only',
                });

                if (response.data.getAllUsers.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: response.data.getAllUsers.message,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                    return;
                }

                this.users = response.data.getAllUsers;

            } catch (error) {
                console.error("Failed to load users:", error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load users. Please try again.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3B82F6',
                });
            }
            this.isLoading = false;
        },
        async loadRoles() {
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_ROLES_QUERY,
                    fetchPolicy: 'network-only',
                });

                if (response.data.getUserRoles.error) {
                    console.error("Error fetching roles:", response.data.getUserRoles.message);
                    return;
                }

                this.roles = response.data.getUserRoles.data;

            } catch (error) {
                console.error("Error fetching roles:", error);
            }
        },
        resetAddUserForm() {
            this.addUserForm = {
                name: '',
                email: '',
                phone_number: '',
                password: '',
                entitySelections: [],
                campusSelections: [],
                role: '',
                department: '',
                position: '',
                is_receive_email: false,
                is_faculty: false,
                faculty_type: '',
                staff_id: '',
            };
            this.addUserCampusOptions = [];
            this.addUserCampusesLoading = false;
        },
        openAddUserModal() {
            if (!this.canCreateUser) {
                return;
            }
            this.resetAddUserForm();
            this.showAddUserModal = true;
            this.loadAddUserOptions();
        },
        closeAddUserModal() {
            this.showAddUserModal = false;
            this.resetAddUserForm();
        },
        async loadAddUserOptions() {
            this.addUserOptionsLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_MANAGEMENT_ADD_USER_OPTIONS,
                    fetchPolicy: 'network-only',
                });
                const payload = response.data.getUserManagementAddUserOptions;
                if (payload.error) {
                    if (this.$swal) {
                        this.$swal({
                            icon: 'error',
                            title: 'Error',
                            text: payload.message,
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3B82F6',
                        });
                    }
                    this.addUserEntityOptions = [];
                    this.addUserCampusOptions = [];
                    this.addUserRolesOptions = [];
                } else if (payload.data) {
                    this.addUserEntityOptions = payload.data.entities || [];
                    this.addUserCampusOptions = [];
                    this.addUserRolesOptions = payload.data.roles || [];
                }
            } catch (error) {
                console.error('Failed to load add user options:', error);
                if (this.$swal) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: 'Could not load form options. Please try again.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                }
            }
            this.addUserOptionsLoading = false;
        },
        pruneAddUserCampusSelections() {
            const options = this.addUserCampusOptions || [];
            const allowed = {};
            var i;
            for (i = 0; i < options.length; i++) {
                allowed[options[i].value] = true;
            }
            const cur = this.addUserForm.campusSelections || [];
            const next = cur.filter(function (x) {
                return allowed[x.value];
            });
            this.addUserForm.campusSelections = next;
        },
        async onAddUserEntitySelectionsChange() {
            if (!this.showAddUserModal) {
                return;
            }
            const sel = this.addUserForm.entitySelections || [];
            const entityVals = sel.map(function (x) {
                return x.value;
            });
            if (!entityVals.length) {
                this.addUserCampusOptions = [];
                this.addUserForm.campusSelections = [];
                this.addUserCampusesLoading = false;
                return;
            }
            this.addUserCampusesLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_USER_MANAGEMENT_CAMPUSES_FOR_ENTITIES,
                    variables: { entities: entityVals },
                    fetchPolicy: 'network-only',
                });
                const payload = response.data.getUserManagementCampusesForEntities;
                if (payload.error) {
                    this.addUserCampusOptions = [];
                    this.addUserForm.campusSelections = [];
                    if (this.$swal) {
                        this.$swal({
                            icon: 'error',
                            title: 'Error',
                            text: payload.message,
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3B82F6',
                        });
                    }
                } else {
                    this.addUserCampusOptions = payload.data || [];
                    this.pruneAddUserCampusSelections();
                }
            } catch (error) {
                console.error('Failed to load campuses:', error);
                this.addUserCampusOptions = [];
                this.addUserForm.campusSelections = [];
                if (this.$swal) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: 'Could not load campuses for the selected entities.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                }
            }
            this.addUserCampusesLoading = false;
        },
        async submitAddUser() {
            if (!this.canCreateUser) {
                return;
            }
            if (this.addUserForm.is_faculty) {
                const ft = this.addUserForm.faculty_type;
                if (!ft || ft === '') {
                    if (this.$swal) {
                        this.$swal({
                            icon: 'warning',
                            title: 'Faculty type required',
                            text: 'Please select Academic, Admin, or Academic Support for faculty type.',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3B82F6',
                        });
                    }
                    return;
                }
            }
            const entityVals = (this.addUserForm.entitySelections || []).map(function (x) {
                return x.value;
            });
            const campusVals = (this.addUserForm.campusSelections || []).map(function (x) {
                return x.value;
            });
            const trimOrNull = function (s) {
                if (s === null || s === undefined) {
                    return null;
                }
                const t = String(s).trim();
                return t === '' ? null : t;
            };
            const input = {
                name: String(this.addUserForm.name).trim(),
                email: String(this.addUserForm.email).trim(),
                password: this.addUserForm.password,
                role: this.addUserForm.role,
                entities: entityVals,
                campuses: campusVals,
                is_receive_email: !!this.addUserForm.is_receive_email,
                is_faculty: !!this.addUserForm.is_faculty,
            };
            input.phone_number = trimOrNull(this.addUserForm.phone_number);
            input.position = trimOrNull(this.addUserForm.position);
            input.department = trimOrNull(this.addUserForm.department);
            if (!input.department) {
                input.department = null;
            }
            if (!input.position) {
                input.position = null;
            }
            if (this.addUserForm.is_faculty) {
                input.faculty_type = trimOrNull(this.addUserForm.faculty_type);
                input.staff_id = trimOrNull(this.addUserForm.staff_id);
            } else {
                input.faculty_type = null;
                input.staff_id = null;
            }
            this.addUserSubmitLoading = true;
            try {
                const response = await this.$apollo.mutate({
                    mutation: CREATE_USER_MANAGEMENT_USER,
                    variables: { input: input },
                });
                const result = response.data.createUserManagementUser;
                if (result.error) {
                    if (this.$swal) {
                        this.$swal({
                            icon: 'error',
                            title: 'Could not create user',
                            text: result.message,
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3B82F6',
                        });
                    }
                } else {
                    if (this.$swal) {
                        this.$swal({
                            icon: 'success',
                            title: 'User created',
                            text: result.message || 'The user was created successfully.',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3B82F6',
                        });
                    }
                    this.closeAddUserModal();
                    this.loadUsers();
                    this.loadUserHighlights();
                }
            } catch (error) {
                console.error('Create user failed:', error);
                const msg = error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message
                    ? error.graphQLErrors[0].message
                    : 'Failed to create user. Please try again.';
                if (this.$swal) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: msg,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                }
            }
            this.addUserSubmitLoading = false;
        },
        onBasicSearch(text) {
            this.searchText = text;
            this.page = 1;
            this.loadUsers();
        },
        onAdvancedSearch(filters) {
            this.advancedSearch = { ...this.advancedSearch, ...filters };
            this.page = 1;
            this.loadUsers();
            this.loadUserHighlights();
        },
        clearSearch() {
            this.searchText = "";
            this.advancedSearch = {
                role: "",
                entity: "",
                campus: "",
            };
            this.page = 1;
            this.loadUsers();
            this.loadUserHighlights();
        },
        onFilterChange() {
            // Don't fetch data immediately, just update the filter values
            // Data will be fetched when Apply button is clicked
        },
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.showFilter = false;
            this.filterByRole = "";
            this.filterByEntity = "";
            this.appliedEntityForDepartments = "";
            this.searchText = "";
            this.page = 1; // Reset to first page when clearing filters
            this.entityDepartmentsWithTopUsers = [];
            this.loadUsers();
            this.loadUserHighlights();
        },
        applyFilters() {
            this.page = 1; // Reset to first page when applying filters
            this.toggleFilter();
            this.loadUsers();
            this.loadUserHighlights();
            if (this.filterByEntity) {
                this.appliedEntityForDepartments = this.filterByEntity;
                this.loadEntityDepartmentsWithTopUsers();
            } else {
                this.appliedEntityForDepartments = '';
                this.entityDepartmentsWithTopUsers = [];
            }
        },
        setPage(page) {
            this.page = page;
            this.loadUsers();
        },
        viewUser(user) {
            this.selectedUser = user;
            this.isViewUser = true;
        },
        closeModal() {
            this.isViewUser = false;
            this.selectedUser = null;
        },
        profilePicture(url) {
            if (!url) {
                return '/img/default-avatar.png';
            }
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        async openDepartmentUsers(dept) {
            this.selectedDepartment = dept;
            this.showDepartmentUsersModal = true;
            this.departmentUsersList = [];
            this.isDepartmentUsersLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_DEPARTMENT_ACTIVE_USERS,
                    variables: {
                        department: dept.label,
                        window: this.highlightWindow,
                        entity: this.filterByEntity || this.advancedSearch.entity || null,
                        role: this.filterByRole || this.advancedSearch.role || null,
                    },
                    fetchPolicy: 'network-only',
                });
                if (response.data.getDepartmentActiveUsers.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: response.data.getDepartmentActiveUsers.message || 'Failed to load users.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                    this.departmentUsersList = [];
                } else {
                    this.departmentUsersList = response.data.getDepartmentActiveUsers.data || [];
                }
            } catch (error) {
                console.error('Failed to load department users:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load users. Please try again.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3B82F6',
                });
                this.departmentUsersList = [];
            }
            this.isDepartmentUsersLoading = false;
        },
        closeDepartmentUsersModal() {
            this.showDepartmentUsersModal = false;
            this.selectedDepartment = null;
            this.departmentUsersList = [];
        },
        onDepartmentChartClick(event, chartContext, config) {
            const idx = config.dataPointIndex;
            const depts = this.entityDepartmentsWithTopUsers || [];
            const dept = depts[idx];
            if (dept) {
                this.openDepartmentUsersFromChart(dept);
            }
        },
        openDepartmentUsersFromChart(dept) {
            this.selectedDepartment = { label: dept.department, value: (dept.topUsers || []).length };
            this.showDepartmentUsersModal = true;
            this.isDepartmentUsersLoading = false;
            this.departmentUsersList = (dept.topUsers || []).map(function (u) {
                return {
                    id: u.user_id,
                    user_id: u.user_id,
                    name: u.user_name,
                    user_name: u.user_name,
                    token: u.token,
                    profile_picture_url: u.profile_picture_url,
                    role: u.role,
                    entity: u.entity,
                    activity_count: u.activity_count,
                };
            });
        },
        async loadEntityDepartmentsWithTopUsers() {
            const entity = this.appliedEntityForDepartments;
            if (!entity) {
                this.entityDepartmentsWithTopUsers = [];
                return;
            }
            this.isEntityDepartmentsLoading = true;
            this.entityDepartmentsWithTopUsers = [];
            try {
                const response = await this.$apollo.query({
                    query: GET_ENTITY_DEPARTMENTS_WITH_TOP_USERS,
                    variables: {
                        entity: entity,
                        window: this.highlightWindow,
                        role: this.filterByRole || this.advancedSearch.role || null,
                        limitPerDepartment: 5,
                    },
                    fetchPolicy: 'network-only',
                });
                if (response.data.getEntityDepartmentsWithTopUsers.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: response.data.getEntityDepartmentsWithTopUsers.message || 'Failed to load departments.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                    this.entityDepartmentsWithTopUsers = [];
                } else {
                    const data = response.data.getEntityDepartmentsWithTopUsers.data;
                    this.entityDepartmentsWithTopUsers = data && data.departments ? data.departments : [];
                }
            } catch (error) {
                console.error('Failed to load entity departments:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load departments. Please try again.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3B82F6',
                });
                this.entityDepartmentsWithTopUsers = [];
            }
            this.isEntityDepartmentsLoading = false;
        },
        editUser() {
            this.$swal({
                icon: 'info',
                title: 'Coming Soon',
                text: 'User editing functionality will be implemented soon.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3B82F6',
            });
        },
        async impersonateUser(user) {
            const result = await this.$swal.fire({
                icon: 'question',
                title: 'Impersonate User',
                text: `Switch to view the platform as ${user.name}?`,
                showCancelButton: true,
                confirmButtonText: 'Switch',
                confirmButtonColor: '#D97706',
                cancelButtonText: 'Cancel',
            });
            if (!result.isConfirmed) {
                return;
            }
            try {
                const response = await this.$store.dispatch('impersonate', { userId: String(user.id) });
                if (response && response.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Impersonation Failed',
                        text: response.message || 'Could not switch to this user.',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3B82F6',
                    });
                    return;
                }
                this.$swal({
                    icon: 'success',
                    title: 'Switched',
                    text: `Now viewing as ${user.name}`,
                    timer: 1500,
                    showConfirmButton: false,
                });
                this.$router.push('/dashboard');
            } catch (err) {
                this.$swal({
                    icon: 'error',
                    title: 'Impersonation Failed',
                    text: err && err.message ? err.message : 'Could not switch to this user.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3B82F6',
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}

.engagement-gradient {
    background: linear-gradient(120deg, #1D337c 0%, #394867 55%, #499DD4 100%);
}
</style>